import { useState, type FormEvent } from 'react';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminFileFolder } from '@mds/shared/types/file';
import Select from '@mds/shared/ui/select';

import Button from '@/components/ui/button';
import Field, { inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import * as files from '@/files/files.api';
import {
  destinationOptions,
  pathLabel,
  subtreeIds,
  toFolderId,
  toFolderValue,
  type FolderTree,
} from '@/files/folders';

/** A new folder goes inside `parentId`; an existing one can be renamed and moved. */
export type FolderDialogMode =
  { kind: 'create'; parentId: string | null } | { kind: 'edit'; folder: AdminFileFolder };

/**
 * Names a new folder, or renames and moves an existing one. Mount it with a
 * `key` per folder so its fields start from that folder each time it opens.
 */
export default function FolderDialog({
  mode,
  tree,
  onClose,
  onSaved,
}: {
  mode: FolderDialogMode;
  tree: FolderTree;
  onClose: () => void;
  onSaved: (folder: AdminFileFolder) => void | Promise<void>;
}) {
  const editing = mode.kind === 'edit' ? mode.folder : null;
  const [name, setName] = useState(editing?.name ?? '');
  const [parent, setParent] = useState(
    toFolderValue(editing ? editing.parentId : (mode as { parentId: string | null }).parentId),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const problem =
    trimmed.length === 0
      ? 'Give the folder a name.'
      : /[/\\]/.test(trimmed)
        ? 'A folder name cannot contain slashes.'
        : trimmed.length > 120
          ? 'Keep the name to 120 characters or fewer.'
          : null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (problem) return setError(problem);
    setSaving(true);
    setError(null);
    try {
      const parentId = toFolderId(parent);
      const saved = editing
        ? await files.updateFolder(editing.id, {
            ...(trimmed !== editing.name ? { name: trimmed } : {}),
            ...(parentId !== editing.parentId ? { parentId } : {}),
          })
        : await files.createFolder(trimmed, parentId);
      await onSaved(saved);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The folder could not be saved. Try again.');
      setSaving(false);
    }
  };

  const unchanged =
    editing !== null && trimmed === editing.name && toFolderId(parent) === editing.parentId;

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.name}` : 'New folder'}
      footer={
        <>
          {/* The footer sits outside the form, so it submits by hand; Enter in the field still works. */}
          <Button disabled={saving || unchanged} onClick={(e) => void submit(e)}>
            {saving ? 'Saving…' : editing ? 'Save folder' : 'Create folder'}
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={(e) => void submit(e)}>
        <Field label="Name" required error={error ?? undefined}>
          <input
            autoFocus
            value={name}
            maxLength={120}
            onChange={(e) => {
              setName(e.target.value);
              setError(null);
            }}
            className={inputCls}
          />
        </Field>

        {editing ? (
          <Field label="Inside" hint="A folder cannot go inside itself or its own subfolders.">
            <Select
              variant="field"
              className={selectCls}
              value={parent}
              onChange={setParent}
              options={destinationOptions(tree, subtreeIds(tree, editing.id))}
            />
          </Field>
        ) : (
          <p className="m-0 text-base text-ink-muted">
            Goes inside <strong className="text-ink">{pathLabel(tree, toFolderId(parent))}</strong>.
          </p>
        )}
      </form>
    </Modal>
  );
}
